import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Crown, Sparkles } from 'lucide-react';
import { trackPremiumClick } from '../utils/analytics';

/*
  PremiumGate — Tek merkezi kilit/blur bileşeni
  
  Kullanım:
    <PremiumGate isPremium={isPremium}>
      <ProtectedContent />
    </PremiumGate>
  
  Props:
    isPremium    — erişim var mı
    mode         — "blur" | "lock" | "hide"
    previewLines — blur modunda kaç satır gösterilecek (default: 3)
    message      — kilit mesajı
    compact      — küçük kart modu
*/

const UPGRADE_MSG = "Detaylı Analizi Gör";

export function PremiumCTA({ message, compact }) {
  const navigate = useNavigate();
  return (
    <div className={`flex flex-col items-center ${compact ? 'gap-2 py-3' : 'gap-3 py-5'}`} data-testid="premium-cta">
      <div className="flex items-center gap-2">
        <Crown className={compact ? 'w-4 h-4' : 'w-5 h-5'} style={{ color: '#FACC15' }} />
        <span className={`font-medium ${compact ? 'text-xs' : 'text-sm'}`} style={{ color: 'rgba(255,255,255,0.7)' }}>
          {message || "Bu özellik Premium üyelik ile açılır"}
        </span>
      </div>
      <button onClick={() => { trackPremiumClick('gate_cta'); navigate('/premium'); }}
        className={`flex items-center gap-2 font-bold text-black transition-all hover:scale-105 hover:-translate-y-0.5 ${compact ? 'text-xs px-4 py-2' : 'text-sm px-6 py-2.5'}`}
        style={{
          background: 'linear-gradient(135deg, #FACC15, #F59E0B)',
          boxShadow: '0 4px 20px rgba(250,204,21,0.3), 0 0 12px rgba(250,204,21,0.15)',
          borderRadius: 4,
        }}
        data-testid="premium-upgrade-btn">
        <Sparkles className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
        {UPGRADE_MSG}
      </button>
    </div>
  );
}

export function PremiumBlur({ children, isPremium, previewLines = 3, message }) {
  if (isPremium) return children;
  return (
    <div className="relative" data-testid="premium-blur-gate">
      {/* Visible preview */}
      <div style={{ maxHeight: `${previewLines * 1.8}em`, overflow: 'hidden', lineHeight: '1.8em' }}>
        {children}
      </div>
      {/* Blur overlay */}
      <div className="absolute inset-0" style={{
        top: `${Math.max(previewLines - 1, 1) * 1.6}em`,
        background: 'linear-gradient(180deg, transparent 0%, rgba(10,18,38,0.7) 30%, rgba(10,18,38,0.95) 100%)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }} />
      {/* CTA */}
      <div className="relative" style={{ marginTop: '-20px' }}>
        <PremiumCTA message={message || "Detaylı doktor analizini görmek için Premium'a geç"} />
      </div>
    </div>
  );
}

export function PremiumLock({ children, isPremium, message, compact }) {
  if (isPremium) return children;
  return (
    <div className="relative" data-testid="premium-lock-gate">
      {/* Dimmed content */}
      <div style={{ opacity: 0.35, pointerEvents: 'none', filter: 'grayscale(0.4)' }}>
        {children}
      </div>
      {/* Lock overlay */}
      <div className="absolute inset-0 flex flex-col items-center justify-center rounded-2xl" style={{
        background: 'rgba(10, 18, 38, 0.6)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
      }}>
        <Lock className="w-6 h-6 mb-2" style={{ color: 'rgba(250,204,21,0.6)' }} />
        <PremiumCTA message={message} compact={compact} />
      </div>
    </div>
  );
}

/*
  PremiumReveal — Başlık ve ilk %30 görünür, geri kalan flu + CTA
  Merak uyandırır, premium'a yönlendirir.
*/
export function PremiumReveal({ children, isPremium, title, icon, message, revealHeight = 320 }) {
  if (isPremium) return children;
  return (
    <div className="relative" data-testid="premium-reveal-gate">
      <div style={{ maxHeight: revealHeight, overflow: 'hidden' }}>
        {children}
      </div>
      {/* Fade overlay — üst kısım net görünür, alt kısım flu */}
      <div className="absolute inset-0" style={{
        top: revealHeight * 0.55,
        background: 'linear-gradient(180deg, transparent 0%, rgba(10,18,38,0.4) 25%, rgba(10,18,38,0.85) 65%, rgba(10,18,38,0.98) 100%)',
        backdropFilter: 'blur(3px)',
        WebkitBackdropFilter: 'blur(3px)',
      }} />
      <div className="relative" style={{ marginTop: -40 }}>
        <PremiumCTA message={message || "Detaylı içeriği görmek için Premium'a geçin"} compact />
      </div>
    </div>
  );
}

export default function PremiumGate({ children, isPremium, mode = 'lock', previewLines, message, compact, title, icon }) {
  if (isPremium) return children;
  if (mode === 'blur') return <PremiumBlur isPremium={isPremium} previewLines={previewLines} message={message}>{children}</PremiumBlur>;
  if (mode === 'reveal') return <PremiumReveal isPremium={isPremium} title={title} icon={icon} message={message}>{children}</PremiumReveal>;
  if (mode === 'hide') return <PremiumCTA message={message} compact={compact} />;
  return <PremiumLock isPremium={isPremium} message={message} compact={compact}>{children}</PremiumLock>;
}
